/**
 * CommandPalette.jsx — CFI-FLOOR-IA-002 / CFI-DIFF-002
 *
 * ⌘K command palette. Built on the `cmdk` primitive (not Radix Dialog)
 * per UIUX-CFI-FLOOR-IA-002 which requires "cmdk primitive under the hood."
 *
 * cmdk provides: fuzzy search, keyboard navigation (↑↓ Enter),
 * grouped results, and sub-100ms open time matching Cursor/Linear.
 *
 * Current state: navigation groups wired (modules, actions).
 * AEGIS entity search (CFI-DIFF-002) is a follow-on session.
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import { Command } from 'cmdk';
import { Search, LayoutDashboard, Settings, X } from 'lucide-react';
import { useCommandPaletteOpen, useUserPrefsStore } from '../stores/userPrefsStore.js';
// Modules sourced from TenantContext (not ModuleLoader) to avoid a circular
// import chain: CommandPalette → ModuleLoader → manifest.js → CommandCenter.jsx
// → TenantContext → (back to shell chunk) which causes TDZ crash at runtime.
import { useTenant } from './TenantContext.jsx';

export function CommandPalette() {
  const isOpen = useCommandPaletteOpen();
  const { setCommandPaletteOpen } = useUserPrefsStore();
  const { tenantConfig } = useTenant();
  // Build a simple nav list from enabled_modules — labels come from manifest
  // metadata we embed here to avoid importing ModuleLoader (circular dep risk).
  const MODULE_LABELS = {
    'command-center': 'Command Center',
    tms: 'TMS', iot: 'IoT', intl: 'International', cx: 'CX',
    crm: 'CRM', oms: 'Orders', wms: 'Warehouse', ims: 'Inventory',
    fleet: 'Fleet', fin: 'Finance', doc: 'Documents', nav: 'Navigation',
    comms: 'Communications', it: 'IT', driver: 'Driver', pylon: 'Pylon',
    aegis: 'AEGIS', settings: 'Settings', intel: 'Intelligence',
  };
  const enabledModules = (tenantConfig?.enabled_modules ?? []).map((id) => ({
    id, label: MODULE_LABELS[id] ?? id, path: `/${id}`,
  }));
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);

  // ⌘K — toggle
  useEffect(() => {
    function onKeyDown(e) {
      const isMac = navigator.platform.startsWith('Mac');
      const modKey = isMac ? e.metaKey : e.ctrlKey;
      if (modKey && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(!isOpen);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, setCommandPaletteOpen]);

  // Focus on open; clear search on close
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 30);
    } else {
      setSearch('');
    }
  }, [isOpen]);

  const handleClose = useCallback(() => setCommandPaletteOpen(false), [setCommandPaletteOpen]);

  if (!isOpen) return null;

  return (
    /* Overlay */
    <div className="elyon-palette-overlay" onClick={handleClose} role="presentation">
      {/* cmdk dialog — stop propagation so clicks inside don't close */}
      <Command
        className="elyon-palette-dialog"
        label="Command palette"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        data-testid="command-palette"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === 'Escape') handleClose(); }}
        shouldFilter={true}
      >
        {/* Visually hidden title for screen readers */}
        <div className="sr-only" id="palette-title">Command palette</div>

        {/* Search input */}
        <div className="elyon-palette-search">
          <Search size={16} strokeWidth={1.5} aria-hidden="true"
            style={{ color: 'var(--color-text-tertiary)', flexShrink: 0 }} />
          <Command.Input
            ref={inputRef}
            className="elyon-palette-input"
            placeholder="Search modules, actions, records…"
            aria-label="Search command palette"
            value={search}
            onValueChange={setSearch}
          />
          <button className="elyon-palette-close-btn" onClick={handleClose}
            aria-label="Close command palette">
            <X size={14} strokeWidth={1.5} aria-hidden="true" />
          </button>
        </div>

        <Command.List className="elyon-palette-list">
          <Command.Empty className="elyon-palette-empty">
            <Search size={20} strokeWidth={1} aria-hidden="true"
              style={{ color: 'var(--color-text-tertiary)' }} />
            <span>No results for "{search}"</span>
          </Command.Empty>

          {/* ── Modules group ── */}
          {enabledModules.length > 0 && (
            <Command.Group heading="Modules" className="elyon-palette-group">
              {enabledModules.map((m) => (
                <Command.Item
                  key={m.id}
                  value={m.label}
                  className="elyon-palette-item"
                  onSelect={() => {
                    window.location.href = m.path;
                    handleClose();
                  }}
                >
                  <LayoutDashboard size={14} strokeWidth={1.5} aria-hidden="true" />
                  <span>{m.label}</span>
                  <span className="elyon-palette-item-type">Module</span>
                </Command.Item>
              ))}
            </Command.Group>
          )}

          {/* ── Actions group ── */}
          <Command.Group heading="Actions" className="elyon-palette-group">
            <Command.Item
              value="Settings"
              className="elyon-palette-item"
              onSelect={() => { window.location.href = '/settings'; handleClose(); }}
            >
              <Settings size={14} strokeWidth={1.5} aria-hidden="true" />
              <span>Settings</span>
              <span className="elyon-palette-item-type">Page</span>
            </Command.Item>
          </Command.Group>
        </Command.List>

        {/* Footer hints */}
        <div className="elyon-palette-footer">
          <span className="elyon-palette-hint"><kbd className="elyon-cmdk-kbd">↑↓</kbd><span>Navigate</span></span>
          <span className="elyon-palette-hint"><kbd className="elyon-cmdk-kbd">↵</kbd><span>Open</span></span>
          <span className="elyon-palette-hint"><kbd className="elyon-cmdk-kbd">Esc</kbd><span>Close</span></span>
        </div>
      </Command>
    </div>
  );
}
